import { useUnit } from 'effector-react';
import { useQuery } from '@tanstack/react-query';
import styled, { css, keyframes } from 'styled-components';
import { Em } from '@/components/primitives';
import { $tasks } from '@/model/processing';
import { $searchStatus, $etaSeconds, type SearchStatus } from '@/model/search';
import { queryFn } from '@/api';
import type { ProcessingTask } from '@/api/mocks/lume';

const STATUS_EYEBROW: Record<SearchStatus, string> = {
  queued: 'в очереди',
  running: 'собираем источники',
  filtering: 'фильтруем находки',
  building: 'строим отчёт',
  completed: 'готово',
  failed: 'ошибка',
  cancelled: 'отменён',
};

const STATUS_LEDE: Record<SearchStatus, string> = {
  queued: 'Lume встал в очередь и ждёт свободный слот.',
  running: 'Lume обходит публичные источники и собирает факты.',
  filtering: 'LLM-фильтр сравнивает находки с вашим профилем и отсекает шум.',
  building: 'Собираем итоговую карту присутствия и пишем саммари.',
  completed: 'Готово — переходим к отчёту.',
  failed: 'Что-то пошло не так. Можно запустить новый поиск.',
  cancelled: 'Поиск отменён.',
};

const formatEta = (sec: number | null): string | null => {
  if (sec === null || !Number.isFinite(sec) || sec <= 0) return null;
  if (sec < 60) return 'осталось меньше минуты';
  const mins = Math.round(sec / 60);
  if (mins < 60) return `осталось ~ ${mins} мин`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `осталось ~ ${hours} ч`;
  const days = Math.round(hours / 24);
  return `осталось ~ ${days} дн`;
};

const breathe = keyframes`0%, 100% { transform: scale(1); } 50% { transform: scale(1.035); }`;
const ripple = keyframes`0% { transform: scale(1); opacity: 0.7; } 100% { transform: scale(1.8); opacity: 0; }`;
const blink = keyframes`0%, 100% { opacity: 0.3; } 50% { opacity: 1; }`;
const rotate = keyframes`to { transform: rotate(360deg); }`;
const fade = keyframes`from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); }`;

const View = styled.section`
  padding: ${({ theme }) => `${theme.spacing[14]} 0`};
  text-align: center;
  min-height: 70vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: radial-gradient(
    800px 500px at 50% 50%,
    ${({ theme }) => theme.fx.ambHaloXSoft},
    transparent 70%
  );
  animation: ${fade} ${({ theme }) => theme.motion.duration.panel} ${({ theme }) => theme.motion.easing.out};
`;

const Pulse = styled.div`
  width: 150px;
  height: 150px;
  border-radius: 50%;
  background: radial-gradient(
    circle at 32% 28%,
    ${({ theme }) => theme.tone.halo.glow} 0%,
    ${({ theme }) => theme.tone.halo.mid} 30%,
    ${({ theme }) => theme.tone.halo.base} 60%,
    ${({ theme }) => theme.tone.halo.deep} 90%
  );
  box-shadow:
    inset -10px -16px 30px ${({ theme }) => theme.fx.insetLo},
    inset 10px 16px 30px ${({ theme }) => theme.fx.insetHi},
    0 0 0 1px ${({ theme }) => theme.tone.halo.deep},
    0 20px 60px -10px ${({ theme }) => theme.fx.glowHaloDeep};
  margin-bottom: ${({ theme }) => theme.spacing[11]};
  position: relative;
  animation: ${breathe} 4s ${({ theme }) => theme.motion.easing.out} infinite;

  &::before,
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    border: 1px solid ${({ theme }) => theme.tone.halo.soft};
    animation: ${ripple} 3s ${({ theme }) => theme.motion.easing.out} infinite;
  }
  &::after {
    animation-delay: 1.5s;
  }
`;

const Eyebrow = styled.div`
  font-family: ${({ theme }) => theme.typography.fontFamilies.mono};
  font-size: ${({ theme }) => theme.typography.fontSizes.monoMd};
  letter-spacing: ${({ theme }) => theme.typography.letterSpacings.widest};
  text-transform: uppercase;
  color: ${({ theme }) => theme.tone.halo.base};
  margin-bottom: ${({ theme }) => theme.spacing[6]};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const Live = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ theme }) => theme.tone.halo.base};
  animation: ${blink} 1.4s ${({ theme }) => theme.motion.easing.out} infinite;
`;

const Title = styled.h3`
  font-family: ${({ theme }) => theme.typography.fontFamilies.serif};
  font-weight: ${({ theme }) => theme.typography.fontWeights.regular};
  font-size: ${({ theme }) => theme.typography.fontSizes.displaySm};
  line-height: ${({ theme }) => theme.typography.lineHeights.tight};
  letter-spacing: ${({ theme }) => theme.typography.letterSpacings.tight};
  margin-bottom: ${({ theme }) => theme.spacing[5]};
  max-width: 720px;
  font-variation-settings: 'opsz' 60;
  padding: 0 ${({ theme }) => theme.spacing[6]};

  @media (max-width: 720px) {
    font-size: ${({ theme }) => theme.typography.fontSizes.hLg};
  }
`;

const Sub = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSizes.bodyLg};
  color: ${({ theme }) => theme.ink[3]};
  max-width: 560px;
  line-height: ${({ theme }) => theme.typography.lineHeights.relaxed};
  margin-bottom: ${({ theme }) => theme.spacing[11]};
`;

const Tasks = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
  width: 100%;
  max-width: 480px;
  text-align: left;
  padding: 0 ${({ theme }) => theme.spacing[6]};
`;

const Task = styled.div<{ $state: 'pending' | 'now' | 'done' }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[4]};
  padding: ${({ theme }) => `${theme.spacing[4]} ${theme.spacing[5]}`};
  background: ${({ theme }) => theme.surface.elev};
  border: 1px solid ${({ theme }) => theme.line.line};
  border-radius: ${({ theme }) => theme.radii.sm};
  font-size: ${({ theme }) => theme.typography.fontSizes.uiLg};
  color: ${({ theme }) => theme.ink[2]};
  transition:
    border-color ${({ theme }) => theme.motion.duration.base} ${({ theme }) => theme.motion.easing.out},
    background ${({ theme }) => theme.motion.duration.base} ${({ theme }) => theme.motion.easing.out};

  ${({ $state, theme }) =>
    $state === 'now' &&
    css`
      border-color: ${theme.tone.halo.soft};
      background: ${theme.tone.halo.tint};
      color: ${theme.ink[1]};
    `}
  ${({ $state, theme }) =>
    $state === 'done' &&
    css`
      color: ${theme.ink[3]};
    `}
`;

const Icon = styled.span<{ $state: 'pending' | 'now' | 'done' }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: ${({ theme }) => theme.typography.fontSizes.monoMd};
  color: ${({ theme }) => theme.surface.elev};

  ${({ $state, theme }) =>
    $state === 'pending' &&
    css`
      background: transparent;
      border: 1.5px dashed ${theme.ink[4]};
    `}
  ${({ $state, theme }) =>
    $state === 'now' &&
    css`
      background: transparent;
      border: 2px solid ${theme.line.line};
      border-top-color: ${theme.tone.halo.base};
      animation: ${rotate} 0.9s linear infinite;
    `}
  ${({ $state, theme }) =>
    $state === 'done' &&
    css`
      background: ${theme.tone.aurora.base};
      &::after {
        content: '✓';
      }
    `}
`;

const Tail = styled.span`
  margin-left: auto;
  font-family: ${({ theme }) => theme.typography.fontFamilies.mono};
  font-size: ${({ theme }) => theme.typography.fontSizes.monoSm};
  color: ${({ theme }) => theme.ink[4]};
`;

export const Processing = () => {
  const [tasks, status, etaSeconds] = useUnit([$tasks, $searchStatus, $etaSeconds]);
  const { data: labels = [] } = useQuery({
    queryKey: ['/api/lume/processing/tasks'],
    queryFn: queryFn<ProcessingTask[]>('/api/lume/processing/tasks'),
  });

  const labelById = new Map(labels.map((l) => [l.id, l.label]));

  // Кнопка «Отменить» убрана преднамеренно: cancel в osint-pipeline не
  // останавливает workers'ов в RabbitMQ-очередях — они продолжают тратить
  // токены и писать findings/facts в БД ещё 5–15 минут после нажатия.
  // До тех пор пока worker'ы не подписываются на fanout `search.cancelled`,
  // даём пользователю экран ожидания без иллюзии управления.

  const eyebrow = status ? STATUS_EYEBROW[status] : 'сборка отчёта';
  const lede = status ? STATUS_LEDE[status] : 'Запускаем поиск…';
  const eta = formatEta(etaSeconds);

  return (
    <View>
      <Pulse />
      <Eyebrow>
        <Live />
        {eyebrow}
        {eta && <span style={{ opacity: 0.7, marginLeft: 8 }}>· {eta}</span>}
      </Eyebrow>
      <Title>
        Пишем вашу <Em>карту</Em>.
      </Title>
      <Sub>{lede}</Sub>
      <Tasks>
        {tasks.map((t) => (
          <Task key={t.id} $state={t.state}>
            <Icon $state={t.state} />
            {labelById.get(t.id) ?? '...'}
            <Tail>{t.progress}%</Tail>
          </Task>
        ))}
      </Tasks>
    </View>
  );
};
